import { useState, useEffect } from "react";
import { Ticket, CheckCircle2, Loader2, Tag, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

interface PromoRedeemProps {
  currentPlan?: string;
}

interface ActivePromo {
  code: string;
  discount_percent: number;
  expires_at: string;
  redeemed_at: string;
}

const PromoCodeRedeem = ({ currentPlan }: PromoRedeemProps) => {
  const { user } = useAuth();
  const [code, setCode] = useState("");
  const [applying, setApplying] = useState(false);
  const [activePromo, setActivePromo] = useState<ActivePromo | null>(null);
  const [loading, setLoading] = useState(true);

  // Check if user already has an active promo
  useEffect(() => {
    if (!user?.id) return;
    const fetchActive = async () => {
      const { data: redemptions } = await supabase
        .from("promo_redemptions")
        .select("*, promo_codes:promo_code_id(code, discount_percent, expires_at)")
        .eq("user_id", user.id)
        .order("redeemed_at", { ascending: false })
        .limit(1);

      if (redemptions && redemptions.length > 0) {
        const r = redemptions[0] as any;
        const promo = r.promo_codes;
        if (promo && new Date(promo.expires_at) > new Date()) {
          setActivePromo({
            code: promo.code,
            discount_percent: promo.discount_percent,
            expires_at: promo.expires_at,
            redeemed_at: r.redeemed_at,
          });
        }
      }
      setLoading(false);
    };
    fetchActive();

    // Listen for realtime changes
    const channel = supabase
      .channel(`promo-${user.id}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "promo_codes" }, () => fetchActive())
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user?.id]);

  const handleApply = async () => {
    if (!code.trim() || !user?.id) return;
    setApplying(true);

    try {
      // Look up the promo code
      const { data: promos } = await supabase
        .from("promo_codes")
        .select("*")
        .eq("code", code.toUpperCase().trim())
        .eq("is_active", true)
        .limit(1);

      if (!promos || promos.length === 0) {
        toast.error("Invalid or expired promo code");
        setApplying(false);
        return;
      }

      const promo = promos[0] as any;

      // Check if expired
      if (new Date(promo.expires_at) < new Date()) {
        toast.error("This promo code has expired");
        setApplying(false);
        return;
      }

      // Check max uses
      if (promo.max_uses && promo.current_uses >= promo.max_uses) {
        toast.error("This promo code has reached its usage limit");
        setApplying(false);
        return;
      }

      // Check if already redeemed by this user
      const { data: existing } = await supabase
        .from("promo_redemptions")
        .select("id")
        .eq("promo_code_id", promo.id)
        .eq("user_id", user.id)
        .limit(1);

      if (existing && existing.length > 0) {
        toast.error("You've already used this promo code");
        setApplying(false);
        return;
      }

      // Redeem it
      const { error: redeemError } = await supabase.from("promo_redemptions").insert({
        promo_code_id: promo.id,
        user_id: user.id,
        discount_percent: promo.discount_percent,
        original_plan: currentPlan || null,
      } as any);

      if (redeemError) {
        toast.error("Failed to apply promo code. Try again.");
        setApplying(false);
        return;
      }

      // Increment usage count via edge function
      await supabase.functions.invoke("redeem-promo", {
        body: { promo_code_id: promo.id },
      });

      setActivePromo({
        code: promo.code,
        discount_percent: promo.discount_percent,
        expires_at: promo.expires_at,
        redeemed_at: new Date().toISOString(),
      });

      toast.success(`🎉 ${promo.discount_percent}% discount applied!`);
      setCode("");
    } catch {
      toast.error("Something went wrong. Try again.");
    }
    setApplying(false);
  };

  const timeLeft = (expiresAt: string) => {
    const diff = new Date(expiresAt).getTime() - Date.now();
    if (diff <= 0) return "Expired";
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    if (days > 0) return `${days}d ${hours % 24}h remaining`;
    return `${hours}h remaining`;
  };

  if (loading) return null;

  return (
    <div className="app-card rounded-2xl p-5">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-[hsl(var(--app-gold)/0.1)] border border-[hsl(var(--app-gold)/0.2)] flex items-center justify-center">
          <Ticket className="w-5 h-5 text-[hsl(var(--app-gold))]" />
        </div>
        <div>
          <h3 className="font-bebas text-base text-[hsl(var(--app-text-primary))]">PROMO CODE</h3>
          <p className="text-[10px] text-[hsl(var(--app-text-muted))]">Enter a code to unlock your discount</p>
        </div>
      </div>

      {activePromo ? (
        <div className="rounded-xl border border-[hsl(var(--app-green)/0.3)] bg-[hsl(var(--app-green)/0.06)] p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-[hsl(var(--app-green))]" />
              <span className="font-mono text-sm font-bold text-[hsl(var(--app-green))] tracking-wider">{activePromo.code}</span>
            </div>
            <span className="px-2 py-0.5 rounded-full bg-[hsl(var(--app-green)/0.15)] text-[hsl(var(--app-green))] text-xs font-bold">
              {activePromo.discount_percent}% OFF
            </span>
          </div>
          <div className="flex items-center justify-between text-[10px] text-[hsl(var(--app-text-muted))]">
            <span>Applied {new Date(activePromo.redeemed_at).toLocaleDateString()}</span>
            <span className="text-[hsl(var(--app-gold))] font-medium">{timeLeft(activePromo.expires_at)}</span>
          </div>
        </div>
      ) : (
        <div className="flex gap-2">
          <input
            value={code}
            onChange={e => setCode(e.target.value.toUpperCase())}
            placeholder="Enter promo code"
            className="flex-1 px-4 py-3 rounded-xl border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-sm text-[hsl(var(--app-text-primary))] font-mono tracking-wider placeholder:text-[hsl(var(--app-text-muted))] placeholder:font-sans placeholder:tracking-normal outline-none focus:border-[hsl(var(--app-gold))] transition-colors"
          />
          <button
            onClick={handleApply}
            disabled={applying || !code.trim()}
            className="px-5 py-3 rounded-xl bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] font-bold text-sm hover:brightness-110 transition-all disabled:opacity-40 flex items-center gap-2 whitespace-nowrap"
          >
            {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ticket className="w-4 h-4" />}
            Apply
          </button>
        </div>
      )}
    </div>
  );
};

export default PromoCodeRedeem;
